import { motion } from "framer-motion";
import { useSettings } from "../contexts/SettingsContext";
import SEO from "../components/SEO";
import SimpleHeader from "../components/SimpleHeader";
import SimpleFooter from "../components/SimpleFooter";

export default function TermsOfService() {
  const { settings } = useSettings();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms of Service for de5.ai - Read our terms and conditions for using the DeFi automation platform."
      />
      <SimpleHeader />
      <main className="min-h-screen bg-white dark:bg-[#0b0f19] text-gray-900 dark:text-white py-16 px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            {/* Title */}
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h1
                className="text-4xl sm:text-5xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Terms of Service
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                These Terms of Service govern your use of de5.ai and our DeFi
                automation platform. Please read these terms carefully before
                using our services.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                Last Updated on August 24, 2024
              </p>
            </motion.div>

            {/* Acceptance of Terms */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Acceptance of Terms
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  By accessing and using de5.ai, you accept and agree to be
                  bound by the terms and provision of this agreement. If you do
                  not agree to abide by the above, please do not use this
                  service.
                </p>
              </div>
            </motion.section>

            {/* Description of Service */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Description of Service
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  de5.ai provides a platform for DeFi automation that allows
                  users to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Create automated DeFi workflows without coding</li>
                  <li>Connect wallets and set up automated triggers</li>
                  <li>
                    Execute DeFi strategies including DCA, swaps, and
                    notifications
                  </li>
                  <li>Monitor and manage automated trading strategies</li>
                  <li>Access real-time market data and analytics</li>
                </ul>
              </div>
            </motion.section>

            {/* User Responsibilities */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                User Responsibilities
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  As a user of our platform, you agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    Provide accurate and complete information when creating an
                    account
                  </li>
                  <li>Maintain the security of your account credentials</li>
                  <li>
                    Use the service in compliance with applicable laws and
                    regulations
                  </li>
                  <li>Not engage in any fraudulent or illegal activities</li>
                  <li>
                    Be responsible for all activities that occur under your
                    account
                  </li>
                  <li>
                    Understand the risks associated with DeFi and automated
                    trading
                  </li>
                </ul>
              </div>
            </motion.section>

            {/* Risk Disclosure */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Risk Disclosure
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  <strong className="text-red-600 dark:text-red-400">
                    Important Risk Warning:
                  </strong>
                </p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  DeFi and cryptocurrency trading involve substantial risk of
                  loss and are not suitable for all investors. You should
                  carefully consider whether trading is suitable for you in
                  light of your circumstances, knowledge, and financial
                  resources.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Market volatility can result in significant losses</li>
                  <li>Smart contract risks and potential bugs</li>
                  <li>Regulatory changes may affect the platform</li>
                  <li>Technical issues or network congestion</li>
                  <li>Liquidity risks in DeFi protocols</li>
                </ul>
              </div>
            </motion.section>

            {/* Prohibited Uses */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Prohibited Uses
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  You may not use our service:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    For any unlawful purpose or to solicit others to perform
                    unlawful acts
                  </li>
                  <li>
                    To violate any international, federal, provincial, or state
                    regulations, rules, laws, or local ordinances
                  </li>
                  <li>
                    To infringe upon or violate our intellectual property rights
                    or the intellectual property rights of others
                  </li>
                  <li>
                    To harass, abuse, insult, harm, defame, slander, disparage,
                    intimidate, or discriminate
                  </li>
                  <li>To submit false or misleading information</li>
                  <li>
                    To upload or transmit viruses or any other type of malicious
                    code
                  </li>
                  <li>
                    To spam, phish, pharm, pretext, spider, crawl, or scrape
                  </li>
                </ul>
              </div>
            </motion.section>

            {/* Limitation of Liability */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Limitation of Liability
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  In no event shall de5.ai, nor its directors, employees,
                  partners, agents, suppliers, or affiliates, be liable for any
                  indirect, incidental, special, consequential, or punitive
                  damages, including without limitation, loss of profits, data,
                  use, goodwill, or other intangible losses, resulting from your
                  use of the service.
                </p>
              </div>
            </motion.section>

            {/* Termination */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Termination
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We may terminate or suspend your account and bar access to the
                  service immediately, without prior notice or liability, under
                  our sole discretion, for any reason whatsoever and without
                  limitation, including but not limited to a breach of the
                  Terms.
                </p>
              </div>
            </motion.section>

            {/* Changes to Terms */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Changes to Terms
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  We reserve the right, at our sole discretion, to modify or
                  replace these Terms at any time. If a revision is material, we
                  will provide at least 30 days notice prior to any new terms
                  taking effect. What constitutes a material change will be
                  determined at our sole discretion.
                </p>
              </div>
            </motion.section>

            {/* Contact Information */}
            <motion.section variants={itemVariants} className="mb-12">
              <h2
                className="text-2xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                Contact Information
              </h2>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  If you have any questions about these Terms of Service, please
                  contact us at{" "}
                  <a
                    href="mailto:contact@de5.ai"
                    className="font-semibold transition-colors"
                    style={{ color: settings.primaryColor }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    contact@de5.ai
                  </a>
                </p>
              </div>
            </motion.section>

            {/* Back to Home Button */}
            <motion.div variants={itemVariants} className="text-center mt-12">
              <motion.button
                onClick={() => (window.location.href = "/")}
                className="px-8 py-4 rounded-xl font-semibold text-lg text-white shadow-lg hover:shadow-xl transition-all duration-300"
                style={{
                  backgroundColor: settings.primaryColor,
                  boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = settings.primaryColor;
                  e.currentTarget.style.boxShadow =
                    "0 20px 40px rgba(233, 92, 73, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = settings.primaryColor;
                  e.currentTarget.style.boxShadow =
                    "0 10px 25px rgba(233, 92, 73, 0.25)";
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Back to Home
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </main>
      <SimpleFooter />
    </>
  );
}
